import type { RefObject } from 'react'
import type { DocModel } from '../lib/documents'
import { docQty } from '../lib/documents'
import { STATUTORY_FALLBACK } from '../lib/cancellationTerms'
import { docFormatDate, docFormatMoney, docT } from '../locales/documents'
import { rksvBelegangaben } from '../lib/rksvBelegangaben'
import { QrCode } from '../ui/QrCode'

/**
 * Das A4-Blatt eines Belegs — die eine Bildschirmfassung.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WARUM DIESE DATEI ENTSTANDEN IST
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Derselbe Beleg wurde an drei Stellen gezeichnet: in `DocumentView`, im
 * Belegbündel und auf der öffentlichen Gastseite. Wer jetzt einen PDF-Zeichner
 * danebenstellt, hat VIER Stellen, an denen dieselbe Rechnung entsteht — und sie
 * laufen still auseinander. Am Bildschirm stimmt der Betrag, im Anhang beim Gast
 * nicht. Das ist der teuerste Fehler, den dieses Produkt machen kann.
 *
 * Also wird die Zahl im selben Zug KLEINER statt größer: `DocumentView` und
 * `InvoiceBundle` benutzen ab jetzt dieses Blatt. (Die Gastseite folgt in einem
 * eigenen Schnitt; sie hat ein eigenes Datenmodell.)
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  UND WARUM ES REIN IST
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Kein Store, kein Router, kein Supabase, kein `useI18n` — die Belegsprache
 * folgt dem GAST und steckt im Modell. Das ist nicht Stilfrage: der Riegel-Test
 * rendert diese Komponente mit `renderToStaticMarkup` OHNE DOM-Umgebung und
 * vergleicht ihren Text mit `belegBloecke()`. Ein Import aus `data/store` liefe
 * dort in einen Kontextfehler, und der Riegel würde „vorübergehend" abgeschaltet.
 *
 * Wer hier eine Zeile ergänzt, muss sie auch in `lib/belegPdf.ts` ergänzen —
 * sonst wird `belegRiegel.test.tsx` rot. Genau dafür ist er da.
 */
export function Belegblatt({ model, rksvQr, blattRef }: {
  model: DocModel
  rksvQr?: string | null
  blattRef?: RefObject<HTMLElement | null>
}) {
  const L = model.lang
  const money = (n: number) => docFormatMoney(L, n, model.currency)
  const date = (iso: string) => docFormatDate(L, iso)
  const title = docT(L, model.kind)

  // Bankverbindung nur, wenn tatsächlich noch etwas zu überweisen ist.
  const settled = model.kind === 'invoice'
    && model.payment.paid > 0 && model.payment.outstanding <= 0.004
  const showBank = Boolean(model.payment.iban) && !settled

  return (
    <article className="doc-sheet" ref={blattRef}>
      {/* ---------- header ---------- */}
      <header className="doc-head">
        <div>
          {model.issuer.logo && (
            <img className="doc-logo" src={model.issuer.logo} alt="" />
          )}
          <div className="doc-issuer-name">{model.issuer.name}</div>
          {model.issuer.lines.map((l, i) => <div key={i}>{l}</div>)}
          {model.issuer.vatId && (
            <div>{docT(L, 'vatId')}: {model.issuer.vatId}</div>
          )}
          {model.issuer.email && <div>{model.issuer.email}</div>}
        </div>
        <div className="doc-title-block">
          <h1>{title}</h1>
          <table>
            <tbody>
              <tr>
                <td>{docT(L, 'number')}</td>
                <td>{model.number}</td>
              </tr>
              <tr>
                <td>{docT(L, 'issueDate')}</td>
                <td>{date(model.issueDate)}</td>
              </tr>
              {model.dueDate && (
                <tr>
                  <td>{docT(L, 'dueDate')}</td>
                  <td>{date(model.dueDate)}</td>
                </tr>
              )}
              {model.validUntil && (
                <tr>
                  <td>{docT(L, 'validUntil')}</td>
                  <td>{date(model.validUntil)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </header>

      {/* ---------- recipient ---------- */}
      <section className="doc-recipient">
        <div className="doc-recipient-name">{model.recipient.name}</div>
        {model.recipient.lines.map((l, i) => <div key={i}>{l}</div>)}
      </section>

      <p className="doc-intro">
        {model.intro
          ? model.intro
          : model.kind === 'quote' ? docT(L, 'quoteIntro') : docT(L, 'invoiceThanks')}
      </p>

      {/* ---------- stay ---------- */}
      {model.stay && (
        <section className="doc-stay">
          <div>
            <span>{docT(L, 'unit')}</span>
            <strong>
              {model.stay.unit}
              {model.stay.property && ` · ${model.stay.property}`}
            </strong>
          </div>
          <div>
            <span>{docT(L, 'stay')}</span>
            <strong>
              {date(model.stay.arrival)} – {date(model.stay.departure)}
              {' · '}{model.stay.nights} {docT(L, 'nights')}
            </strong>
          </div>
          <div>
            <span>{docT(L, 'guests')}</span>
            <strong>
              {model.stay.adults} {docT(L, 'adults')}
              {model.stay.children > 0 && `, ${model.stay.children} ${docT(L, 'children')}`}
            </strong>
          </div>
        </section>
      )}

      {/* ---------- line items ---------- */}
      <table className="doc-lines">
        <thead>
          <tr>
            <th className="l">{docT(L, 'position')}</th>
            <th className="r">{docT(L, 'qty')}</th>
            <th className="r">{docT(L, 'unitPrice')}</th>
            <th className="r">{docT(L, 'vat')}</th>
            <th className="r">{docT(L, 'gross')}</th>
          </tr>
        </thead>
        <tbody>
          {model.lines.map((l, i) => (
            <tr key={i}>
              <td className="l">{l.label}</td>
              <td className="r">{docQty(l.qty)}</td>
              <td className="r">{money(l.unitPrice)}</td>
              <td className="r">{l.vatRate > 0 ? `${docQty(l.vatRate)} %` : '—'}</td>
              <td className="r">{money(l.gross)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ---------- totals & VAT summary ---------- */}
      <section className="doc-totals">
        <table className="doc-vat">
          <caption>{docT(L, 'vatSummary')}</caption>
          <thead>
            <tr>
              <th className="l">{docT(L, 'rate')}</th>
              <th className="r">{docT(L, 'net')}</th>
              <th className="r">{docT(L, 'vat')}</th>
            </tr>
          </thead>
          <tbody>
            {model.vatBuckets.map((b) => (
              <tr key={b.rate}>
                <td className="l">{docQty(b.rate)} %</td>
                <td className="r">{money(b.net)}</td>
                <td className="r">{money(b.vat)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <table className="doc-sum">
          <tbody>
            <tr>
              <td>{docT(L, 'totalNet')}</td>
              <td className="r">{money(model.totals.net)}</td>
            </tr>
            <tr>
              <td>{docT(L, 'totalVat')}</td>
              <td className="r">{money(model.totals.vat)}</td>
            </tr>
            <tr className="grand">
              <td>{docT(L, 'totalGross')}</td>
              <td className="r">{money(model.totals.gross)}</td>
            </tr>
            {model.payment.paid > 0 && (
              <>
                {/* Die Anzahlung zuerst und benannt — sie ist der Betrag, an den
                    sich der Gast erinnert. Was darüber hinaus schon bezahlt wurde,
                    steht darunter, damit die Summen nachvollziehbar bleiben. */}
                {model.kind === 'invoice' && model.payment.prepayment > 0 && (
                  <tr>
                    <td>{docT(L, 'prepaymentReceived')}</td>
                    <td className="r">−{money(model.payment.prepayment)}</td>
                  </tr>
                )}
                {model.payment.paid - (model.kind === 'invoice' ? model.payment.prepayment : 0) > 0 && (
                <tr>
                  <td>{docT(L, 'paid')}</td>
                  <td className="r">
                    −{money(model.payment.paid
                      - (model.kind === 'invoice' ? model.payment.prepayment : 0))}
                  </td>
                </tr>
                )}
                <tr className="grand">
                  <td>{docT(L, 'outstanding')}</td>
                  <td className="r">{money(model.payment.outstanding)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </section>

      {/*
        Die Quittungszeile. Sie steht direkt unter den Summen, weil dort der Blick
        landet, und sie erscheint nur, wenn wirklich nichts mehr offen ist —
        „dankend erhalten" bei € 300 Restbetrag wäre eine Falschaussage.
      */}
      {model.kind === 'invoice' && model.payment.outstanding <= 0.004
        && model.payment.paid > 0 && model.payment.paidOn && (
        <p className="doc-paid">
          {docT(L, 'paidInFull')}
          {' '}{date(model.payment.paidOn)}
          {model.payment.paidMethod
            && docT(L, `payMethod_${model.payment.paidMethod}` as never)
            && ` · ${docT(L, `payMethod_${model.payment.paidMethod}` as never)}`}
        </p>
      )}

      {model.vatExemptNote && (
        <p className="doc-note">{docT(L, 'smallBusiness')}</p>
      )}

      {/* ---------- payment ----------
          Die Bankverbindung verschwindet, sobald nichts mehr offen ist. „Bitte
          überweisen Sie den Betrag" unter einer bezahlten Rechnung ist keine
          Kleinigkeit: der Gast überweist ein zweites Mal, und der Vermieter darf
          das Geld zurückschicken. Die Kaution bleibt sichtbar, die ist ein
          eigener Vorgang. */}
      {(showBank || model.payment.prepayment > 0 || model.payment.deposit > 0
        || model.kind === 'quote') && (
      <section className="doc-payment">
        <h2>{docT(L, 'payment')}</h2>
        {model.kind !== 'invoice' && model.payment.prepayment > 0 && (
          <p>
            <strong>{docT(L, 'prepayment')}: {money(model.payment.prepayment)}</strong>
            {model.payment.prepaymentDue &&
              ` · ${docT(L, 'prepaymentDue')} ${date(model.payment.prepaymentDue)}`}
          </p>
        )}
        {showBank && (
          <>
            <p>{docT(L, 'bankTransfer')}</p>
            <table className="doc-bank">
              <tbody>
                {model.payment.accountHolder && (
                  <tr>
                    <td>{docT(L, 'accountHolder')}</td>
                    <td>{model.payment.accountHolder}</td>
                  </tr>
                )}
                <tr><td>IBAN</td><td>{model.payment.iban}</td></tr>
                {model.payment.bic && <tr><td>BIC</td><td>{model.payment.bic}</td></tr>}
                <tr>
                  <td>{docT(L, 'reference')}</td>
                  <td>{model.payment.reference}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}
        {model.payment.deposit > 0 && (
          <p className="doc-note">
            <strong>{docT(L, 'deposit')}: {money(model.payment.deposit)}</strong>
            {' — '}{docT(L, 'depositNote')}
          </p>
        )}
        {/* Zwei verschiedene Dinge, die vorher um dieselbe Zeile konkurrierten:
            wie lange das Angebot gilt, und was bei einer Absage passiert. Die
            Gültigkeit steht immer; die Stornobedingungen nur, wenn welche
            vereinbart sind — sonst der Verweis auf das Gesetz. */}
        {model.kind === 'quote' && (
          <>
            <p className="doc-note">{docT(L, 'quoteTerms')}</p>
            <p className="doc-note whitespace-pre-line">
              {model.terms?.trim() || STATUTORY_FALLBACK[L]}
            </p>
          </>
        )}
      </section>
      )}

      {rksvQr && (
        <section className="doc-rksv">
          <QrCode value={rksvQr} size={104} />
          <div className="doc-rksv-note">
            <strong>Registrierkasse (RKSV)</strong>
            {/* Belegangaben als Text (§ 132a BAO): die Kassenidentifikations-
                nummer darf nicht nur im QR stecken. */}
            {(() => {
              const angaben = rksvBelegangaben(rksvQr)
              return angaben && (
                <span>
                  Kassenidentifikationsnummer: {angaben.kassenId} ·{' '}
                  RKSV-Belegnummer: {angaben.belegNr}
                </span>
              )
            })()}
            <span>
              Dieser Beleg ist signiert. Der QR-Code kann mit der App
              „BMF Belegcheck" geprüft werden.
            </span>
          </div>
        </section>
      )}

      {/* The document's own footer. The browser prints its URL and the date down
          there unless the landlord turns headers off; this is what SHOULD be at
          the foot of an invoice, and it belongs to the document rather than to
          the browser. */}
      <footer className="doc-print-foot">
        {[model.issuer.name, model.issuer.vatId && `${docT(L, 'vatId')}: ${model.issuer.vatId}`,
          model.number].filter(Boolean).join(' · ')}
      </footer>
    </article>
  )
}
